/**
 * Payment Methods Strip — Shows Tabby, Tamara, Visa, Mastercard, Apple Pay, Cash
 * Brand Colors: #D4A843 (gold)
 */
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const TABBY_LOGO = "/manus-storage/tabby-logo-transparent_c799f0ee.webp";
const TAMARA_LOGO = "/manus-storage/tamara-logo-en_99665589.webp";
const BRAND_GOLD = "#D4A843";

export function PaymentMethodsStrip() {
  const { t } = useLanguage();
  return (
    <section className="py-5 bg-[#FBF8F1] border-y border-gray-100 overflow-hidden">
      <div className="container">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
          {/* Label */}
          <div className="flex items-center gap-2 shrink-0">
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5 shrink-0"
              style={{ color: BRAND_GOLD }}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            <span className="text-xs font-semibold tracking-[0.15em] uppercase text-gray-500">
              {t("FLEXIBLE PAYMENT OPTIONS", "\u062e\u064a\u0627\u0631\u0627\u062a \u062f\u0641\u0639 \u0645\u0631\u0646\u0629")}
            </span>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-8 bg-gray-200" />

          {/* Payment Logos */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center">
            {/* Tabby */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center justify-center bg-white rounded-lg px-3 py-1.5 md:px-4 md:py-2 border border-gray-100 shadow-sm hover:shadow-md transition-shadow w-[70px] h-[28px] md:w-[85px] md:h-[30px]"
            >
              <img src={TABBY_LOGO} alt="Tabby - Buy Now Pay Later" width="50" height="20" style={{ maxWidth: '50px', maxHeight: '20px' }} className="h-4 md:h-5 w-auto" />
            </motion.div>

            {/* Tamara */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center justify-center bg-white rounded-lg px-3 py-1.5 md:px-4 md:py-2 border border-gray-100 shadow-sm hover:shadow-md transition-shadow w-[70px] h-[28px] md:w-[85px] md:h-[30px]"
            >
              <img src={TAMARA_LOGO} alt="\u062a\u0645\u0627\u0631\u0627 - Tamara" width="62" height="20" style={{ maxWidth: '60px', maxHeight: '20px' }} className="h-4 md:h-5 w-auto" />
            </motion.div>

            {/* Visa */}
            <div className="flex items-center bg-white rounded-lg px-2 py-1.5 md:px-3 md:py-2 border border-gray-100 shadow-sm">
              <svg viewBox="0 0 48 32" className="h-5 md:h-6 w-auto">
                <rect width="48" height="32" rx="4" fill="#1A1F71" />
                <text x="24" y="21" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text>
              </svg>
            </div>

            {/* Mastercard */}
            <div className="flex items-center bg-white rounded-lg px-2 py-1.5 md:px-3 md:py-2 border border-gray-100 shadow-sm">
              <svg viewBox="0 0 48 32" className="h-5 md:h-6 w-auto">
                <rect width="48" height="32" rx="4" fill="#252525" />
                <circle cx="19" cy="16" r="8" fill="#EB001B" />
                <circle cx="29" cy="16" r="8" fill="#F79E1B" />
                <path d="M24 9.8a8 8 0 010 12.4 8 8 0 000-12.4z" fill="#FF5F00" />
              </svg>
            </div>

            {/* Apple Pay */}
            <div className="flex items-center bg-white rounded-lg px-2 py-1.5 md:px-3 md:py-2 border border-gray-100 shadow-sm">
              <svg viewBox="0 0 48 32" className="h-5 md:h-6 w-auto">
                <rect width="48" height="32" rx="4" fill="#000" />
                <g fill="white">
                  <path d="M15.2 10.5c-.6.7-1.5 1.2-2.4 1.1-.1-1 .4-2 .9-2.6.6-.7 1.5-1.2 2.3-1.2.1 1-.3 2-.8 2.7zm.8 1.4c-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.7-2.7-.7-1.4 0-2.7.8-3.4 2.1-1.4 2.5-.4 6.2 1 8.2.7 1 1.5 2.1 2.6 2.1 1 0 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1 0 1.8-1 2.5-2 .5-.7.8-1.2 1.1-1.9-2.5-1-2.9-4.7-.4-6.1-.7-.9-1.8-1.5-3-1.5z" />
                  <text x="33" y="20" textAnchor="middle" fontSize="8" fontWeight="600" fontFamily="Arial, sans-serif">Pay</text>
                </g>
              </svg>
            </div>

            {/* Cash */}
            <div className="flex items-center bg-white rounded-lg px-2 py-1.5 md:px-3 md:py-2 border border-gray-100 shadow-sm">
              <svg viewBox="0 0 48 32" className="h-5 md:h-6 w-auto">
                <rect width="48" height="32" rx="4" fill="#16a34a" />
                <text x="24" y="20" textAnchor="middle" fill="white" fontSize="9" fontWeight="600" fontFamily="Arial, sans-serif">CASH</text>
              </svg>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-8 bg-gray-200" />

          {/* BNPL Message */}
          <div className="text-center md:text-start shrink-0">
            <p className="text-xs font-semibold text-gray-700">
              {t("Split into 4 interest-free payments", "\u0642\u0633\u0651\u0645 \u0639\u0644\u0649 4 \u062f\u0641\u0639\u0627\u062a \u0628\u062f\u0648\u0646 \u0641\u0648\u0627\u0626\u062f")}
            </p>
            <p className="text-[10px] text-gray-400">
              {t("with Tabby & Tamara", "\u0639\u0628\u0631 \u062a\u0627\u0628\u064a \u0648\u062a\u0645\u0627\u0631\u0627")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
